/**
 * Email-template configuration of the tax-receipt module: two independent contexts, each with a
 * default template and ordered personalisation rules (first match wins). Mirrors OPDON
 * `TaxReceiptEmailContext`, `TaxReceiptEmailDonorTypeFilter` and `TaxReceiptEmailDonationTypeFilter`.
 */
/** Wire names of the two contexts — the route segment of the PUT and the DTO discriminator. */
export const TAX_RECEIPT_EMAIL_CONTEXTS = ["UnitSend", "AnnualCampaign"];
/** Mirrors OPDON `TaxReceiptEmailContextValues.MaxSubjectLength` (the template cap; the composed subject is cut to the same length). */
export const TAX_RECEIPT_EMAIL_SUBJECT_MAX_LENGTH = 500;
export const TAX_RECEIPT_EMAIL_DONOR_TYPE_FILTERS = ["All", "Individual", "Organization"];
export const TAX_RECEIPT_EMAIL_DONATION_TYPE_FILTERS = ["All", "OneOff", "Regular"];
/** Mirrors `TaxReceiptMergeTags.All` (Opink.Contracts): the only tokens OPDON resolves in a subject. */
export const TAX_RECEIPT_SUBJECT_MERGE_TAGS = [
    "receipt_number",
    "receipt_issued_at",
    "receipt_amount",
    "receipt_fiscal_year",
    "receipt_donor_name",
    "receipt_issuer_name",
    "receipt_link",
];
const isSubjectMergeTag = (key) => TAX_RECEIPT_SUBJECT_MERGE_TAGS.includes(key.trim().toLowerCase());
/**
 * Mirrors OPDON's subject rules, length on the trimmed value. Like OPDON, any braced text that is not
 * `{{key}}` with one of the seven keys is refused: a near-token such as `{{ receipt_number }}` would
 * otherwise reach the donor as typed. Once the tokens are set aside, a stray `{{` / `}}`, a key in single
 * braces (`{receipt_number}`) or against a lone one (`{receipt_number`), or a third brace against a token
 * (`{{{receipt_number}}}`) is refused for the same reason; other single-braced text is wording.
 */
export function taxReceiptEmailSubjectError(subject) {
    const trimmed = subject.trim();
    if (trimmed === "" || trimmed.length > TAX_RECEIPT_EMAIL_SUBJECT_MAX_LENGTH)
        return "required";
    const braced = trimmed.match(/\{\{[^{}]*\}\}/g) ?? [];
    const known = (token) => /^\{\{[a-z0-9_]+\}\}$/i.test(token) && isSubjectMergeTag(token.slice(2, -2));
    if (!braced.every(known))
        return "unknownVariable";
    if (/\{\{\{[a-z0-9_]+\}\}|\{\{[a-z0-9_]+\}\}\}/i.test(trimmed))
        return "malformedVariable";
    const rest = trimmed.replace(/\{\{[a-z0-9_]+\}\}/gi, " ");
    const singleBraced = [...rest.matchAll(/\{([^{}]*)\}/g)].map((m) => m[1]);
    if (rest.includes("{{") || rest.includes("}}") || singleBraced.some(isSubjectMergeTag))
        return "malformedVariable";
    const unpaired = rest.replace(/\{[^{}]*\}/g, " ");
    const againstLoneBrace = [...unpaired.matchAll(/\{\s*([a-z0-9_]+)|([a-z0-9_]+)\s*\}/gi)].map((m) => m[1] ?? m[2]);
    return againstLoneBrace.some(isSubjectMergeTag) ? "malformedVariable" : null;
}
export function isTaxReceiptEmailSubjectInvalid(subject) {
    return taxReceiptEmailSubjectError(subject) !== null;
}
export function isTaxReceiptEmailContextKey(value) {
    return TAX_RECEIPT_EMAIL_CONTEXTS.includes(value);
}
export function isTaxReceiptEmailDonorTypeFilter(value) {
    return TAX_RECEIPT_EMAIL_DONOR_TYPE_FILTERS.includes(value);
}
export function isTaxReceiptEmailDonationTypeFilter(value) {
    return TAX_RECEIPT_EMAIL_DONATION_TYPE_FILTERS.includes(value);
}
export function createEmptyTaxReceiptEmailContext(context) {
    return { context, defaultTemplateId: null, subject: "", rules: [], updatedAt: null };
}
export function createEmptyTaxReceiptEmailContexts() {
    return {
        UnitSend: createEmptyTaxReceiptEmailContext("UnitSend"),
        AnnualCampaign: createEmptyTaxReceiptEmailContext("AnnualCampaign"),
    };
}
